import React, { useEffect, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Box, Button, TextField, Typography, Alert, MenuItem, IconButton, Autocomplete } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { invoicesAPI, clientsAPI, projectsAPI, timesheetsAPI, employeesAPI } from '../../services/api';
import { CURRENCIES, NAVIGATION, UNITS, INVOICE_NOTES, INVOICE_STATUSES } from '../../constants';
import { generateAndUploadInvoicePDF } from '../../utils/invoiceUtils';
import { formatNumber } from '../../utils/formatNumber';
import { getMonthOptions, getCurrentMonthValue } from '../../constants/months';

const schema = z.object({
  invoiceNumber: z.string().min(1, 'Invoice number is required'),
  clientId: z.string().min(1, 'Client is required'),
  projectId: z.string().optional(),
  issueDate: z.string().optional(),
  dueDate: z.string().min(1, 'Due date is required'),
  currency: z.string().min(1, 'Currency is required'),
  notes: z.string().optional(),
  status: z.string().optional(),
  items: z.array(z.object({
    description: z.string().min(1, 'Description is required'),
    unitPrice: z.string().min(1, 'Price is required'),
    quantity: z.string().min(1, 'Quantity is required'),
    unit: z.string(),
    amount: z.number().optional()
  })).min(1, 'At least one item is required'),
  paidDate: z.string().optional(),
  rsdAmount: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

const AddInvoice: React.FC = () => {
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors, isSubmitting }, watch, setValue, control } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      invoiceNumber: '',
      clientId: '',
      projectId: '',
      issueDate: '',
      dueDate: '',
      currency: CURRENCIES.EUR,
      notes: '',
      status: INVOICE_STATUSES.DRAFT,
      items: [
        {
          description: '',
          unitPrice: '',
          quantity: '',
          unit: UNITS.HOUR,
          amount: 0
        }
      ],
      paidDate: '',
      rsdAmount: '',
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items'
  });

  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [clients, setClients] = useState<any[]>([]);
  const [projects, setProjects] = useState<{ id: string, name: string, clientId: string, currency: string, isActive?: boolean }[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<{ id: string, name: string, clientId: string, currency: string, isActive?: boolean }[]>([]);
  const [employees, setEmployees] = useState<{ id: string, firstName: string, lastName: string }[]>([]);
  
  // Month filter state (not stored in database)
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthValue);

  const watchedItems = watch('items');
  const watchedCurrency = watch('currency');
  const watchedClientId = watch('clientId');
  const watchedProjectId = watch('projectId');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clientsData, projectsData, employeesData] = await Promise.all([
          clientsAPI.getAll(),
          projectsAPI.getAll(),
          employeesAPI.getAll()
        ]);
        setClients(clientsData.filter((client: { isActive?: boolean }) => client.isActive));
        setProjects(projectsData.filter((project: { isActive?: boolean }) => project.isActive));
        setEmployees(employeesData);
        
        // Get next invoice number
        const now = new Date();
        const year = parseInt(now.getFullYear().toString().slice(-2));
        const invoices = await invoicesAPI.getAll();
        const yearInvoices = invoices.filter((inv: any) => {
          const invYear = parseInt(inv.invoiceNumber?.split('/')[1]);
          return invYear === year;
        });
        const lastInvoice = yearInvoices.length > 0 ? Math.max(...yearInvoices.map((inv: any) => parseInt(inv.invoiceNumber?.split('/')[0]))) : 0;
        const nextNumber = lastInvoice + 1;
        const nextInvoiceNum = `${nextNumber}/${year}`;
        setValue('invoiceNumber', nextInvoiceNum);
        setValue('notes', INVOICE_NOTES.DEFAULT);
      } catch (error) {
        const now = new Date();
        const year = parseInt(now.getFullYear().toString().slice(-2));
        const nextInvoiceNum = `1/${year}`;
        setValue('invoiceNumber', nextInvoiceNum);
        setValue('notes', INVOICE_NOTES.DEFAULT);
      }
    };
    fetchData();
  }, []);

  // Filter projects by selected client
  useEffect(() => {
    if (watchedClientId) {
      const clientProjects = projects.filter(
        (project) => project.clientId === watchedClientId && project.isActive !== false
      );
      setFilteredProjects(clientProjects);
      // Reset project selection if current project is not for selected client
      const currentProjectId = watch('projectId');
      if (currentProjectId && !clientProjects.find(p => p.id === currentProjectId)) {
        setValue('projectId', '');
      }
    } else {
      setFilteredProjects([]);
      setValue('projectId', '');
    }
  }, [watchedClientId, projects, setValue, watch]);

  // Auto-populate currency from selected project
  useEffect(() => {
    if (watchedProjectId) {
      const selectedProject = projects.find(p => p.id === watchedProjectId);
      if (selectedProject && selectedProject.currency) {
        setValue('currency', selectedProject.currency);
      }
    }
  }, [watchedProjectId, projects, setValue]);

  // Fetch timesheet data and prepopulate invoice items when project is selected
  useEffect(() => {
    const fetchProjectTimesheets = async () => {
      if (!watchedProjectId || !selectedMonth) return;
      
      try {
        // Calculate start and end dates for the selected month
        const [year, month] = selectedMonth.split('-').map(Number);
        const startDate = new Date(year, month - 1, 1).toISOString();
        const endDate = new Date(year, month, 0, 23, 59, 59, 999).toISOString();
        
        // Get timesheets for the selected project and month
        const timesheets = await timesheetsAPI.getAll({ 
          projectId: watchedProjectId,
          startDate,
          endDate
        });
        
        if (timesheets && timesheets.length > 0) {
          // Get project details to access employee rates
          const selectedProject = projects.find(p => p.id === watchedProjectId);
          let projectEmployees: any[] = [];
          
          if (selectedProject) {
            try {
              const projectDetails = await projectsAPI.getById(selectedProject.id);
              projectEmployees = projectDetails.employees || [];
            } catch (error) {
              console.error('Error fetching project details:', error);
            }
          }
          
          // Group timesheets by employee and calculate total hours
          const employeeHours: { [key: string]: { hours: number, employee: any, rate: number } } = {};
          
          timesheets.forEach((timesheet: any) => {
            const employeeId = timesheet.employeeId;
            const hours = timesheet.hours || 0;
            
            if (!employeeHours[employeeId]) {
              const employee = employees.find(emp => emp.id === employeeId);
              // Find the project-specific rate for this employee
              // Try both employeeId and id fields since the structure might vary
              const projectEmployee = projectEmployees.find(pe => 
                pe.employeeId === employeeId || pe.id === employeeId || pe.employee?.id === employeeId
              );
              const hourlyRate = projectEmployee?.price || 50; // Default to 50 if no rate found
              
              employeeHours[employeeId] = {
                hours: 0,
                employee: employee,
                rate: hourlyRate
              };
            }
            employeeHours[employeeId].hours += hours;
          });
          
          // Create invoice items from timesheet data
          const newItems = Object.values(employeeHours)
            .filter(item => item.employee && item.hours > 0)
            .map(item => ({
              description: `${item.employee.firstName} ${item.employee.lastName}`,
              unitPrice: item.rate.toString(),
              quantity: item.hours.toString(),
              unit: UNITS.HOUR,
              amount: item.rate * item.hours
            }));
          
          if (newItems.length > 0) {
            // Replace existing items with timesheet-based items
            setValue('items', newItems);
          }
        }
      } catch (error) {
        console.error('Error fetching timesheet data:', error);
      }
    };
    
    fetchProjectTimesheets();
  }, [watchedProjectId, selectedMonth, employees, setValue]);

  // Calculate amounts when price or quantity changes
  useEffect(() => {
    if (watchedItems) {
      watchedItems.forEach((item, index) => {
        const price = parseFloat(item.unitPrice) || 0;
        const quantity = parseFloat(item.quantity) || 0;
        const amount = Math.round((price * quantity) * 100) / 100; // Round to 2 decimal places
        setValue(`items.${index}.amount`, amount);
      });
    }
  }, [watchedItems, setValue]);

  const addItem = () => {
    append({
      description: '',
      unitPrice: '',
      quantity: '',
      unit: UNITS.HOUR,
      amount: 0
    });
  };

  const removeItem = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const calculateSubtotal = () => {
    if (!watchedItems) return 0;
    return watchedItems.reduce((total, item) => {
      const amount = item.amount || 0;
      return total + amount;
    }, 0);
  };

  const calculatePDV = () => {
    const selectedClient = clients.find(client => client.id === watchedClientId);
    const isDomestic = selectedClient?.isDomestic || false;
    return isDomestic ? calculateSubtotal() * 0.2 : 0;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculatePDV();
  };

  const onSubmit = async (data: FormData) => {
    setSuccess(''); 
    setError('');
    try {
      const payload: any = {
        invoiceNumber: data.invoiceNumber,
        clientId: data.clientId,
        dueDate: data.dueDate ? new Date(data.dueDate).toISOString() : undefined,
        currency: data.currency,
        items: data.items.map(item => ({
          description: item.description,
          unitPrice: parseFloat(item.unitPrice),
          quantity: parseFloat(item.quantity),
          unit: item.unit,
          amount: parseFloat(item.unitPrice) * parseFloat(item.quantity)
        }))
      };
      
      if (data.projectId) payload.projectId = data.projectId;
      if (data.issueDate) payload.issueDate = new Date(data.issueDate).toISOString();
      if (data.notes) payload.notes = data.notes;
      if (data.status) payload.status = data.status;
      if (data.rsdAmount && data.rsdAmount.trim()) payload.rsdAmount = parseFloat(data.rsdAmount);
      
      // Handle paid date - send null if empty, otherwise send the date
      if (data.paidDate && data.paidDate.trim()) {
        payload.paidDate = new Date(data.paidDate).toISOString();
      } else {
        payload.paidDate = null;
      }
      
      const response = await invoicesAPI.create(payload);
      const invoiceId = response.invoice?.id;
      
      setSuccess('Invoice added successfully!');
      
      // Generate PDF and upload it as a file (with a small delay to ensure invoice is fully created)
      try {
        if (!invoiceId) {
          throw new Error('Invoice ID not found in response');
        }
        
        // Add a small delay to ensure the invoice is fully committed to the database
        await new Promise(resolve => setTimeout(resolve, 1000));
        const pdfGenerated = await generateAndUploadInvoicePDF(invoiceId, data.invoiceNumber);
        
        if (pdfGenerated) {
          setSuccess('Invoice added successfully and PDF generated!');
        } else {
          setSuccess('Invoice added successfully, but PDF generation failed. You can generate it later from the invoice details.');
        }
      } catch (pdfError) {
        setSuccess('Invoice added successfully, but PDF generation failed. You can generate it later from the invoice details.');
      }
      
      setTimeout(() => navigate(NAVIGATION.INVOICES), 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add invoice');
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, px: { xs: 2, md: 0 } }}>
      <Typography variant="h5" fontWeight={700} mb={2}>Add New Invoice</Typography>
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 2 }}>
            <TextField 
              label="Invoice Number" 
              fullWidth 
              {...register('invoiceNumber')} 
              error={!!errors.invoiceNumber} 
              helperText={errors.invoiceNumber?.message}
              value={watch('invoiceNumber') || ''}
            />
            <Autocomplete
              options={clients}
              getOptionLabel={(option) => option.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={clients.find((client) => client.id === watchedClientId) || null}
              onChange={(_, newValue) => setValue('clientId', newValue?.id || '', { shouldValidate: true })}
              sx={{ width: '100%' }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Client"
                  fullWidth
                  error={!!errors.clientId}
                  helperText={errors.clientId?.message}
                />
              )}
            />
            <Autocomplete
              options={filteredProjects}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={filteredProjects.find((project) => project.id === watchedProjectId) || null}
              onChange={(_, newValue) => setValue('projectId', newValue?.id || '')}
              disabled={!watchedClientId}
              sx={{ width: '100%' }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Project"
                  fullWidth
                  error={!!errors.projectId}
                  helperText={
                    errors.projectId?.message ||
                    (!watchedClientId
                      ? 'Select a client first'
                      : filteredProjects.length === 0
                        ? 'No active projects for this client'
                        : undefined)
                  }
                />
              )}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 2 }}>
            <TextField 
              label="Currency" 
              select 
              fullWidth 
              {...register('currency')} 
              error={!!errors.currency} 
              helperText={errors.currency?.message}
              value={watch('currency') || CURRENCIES.EUR}
            >
              <MenuItem value={CURRENCIES.EUR}>{CURRENCIES.EUR} (€)</MenuItem>
              <MenuItem value={CURRENCIES.USD}>{CURRENCIES.USD} ($)</MenuItem>
              <MenuItem value={CURRENCIES.GBP}>{CURRENCIES.GBP} (£)</MenuItem>
              <MenuItem value={CURRENCIES.RSD}>{CURRENCIES.RSD} (din)</MenuItem>
              <MenuItem value={CURRENCIES.CHF}>{CURRENCIES.CHF} (CHF)</MenuItem>
            </TextField>
            <TextField 
              label="Status" 
              select 
              fullWidth 
              {...register('status')} 
              error={!!errors.status} 
              helperText={errors.status?.message}
              value={watch('status') || INVOICE_STATUSES.DRAFT}
            >
              <MenuItem value={INVOICE_STATUSES.DRAFT}>{INVOICE_STATUSES.DRAFT}</MenuItem>
              <MenuItem value={INVOICE_STATUSES.SENT}>{INVOICE_STATUSES.SENT}</MenuItem>
              <MenuItem value={INVOICE_STATUSES.PAID}>{INVOICE_STATUSES.PAID}</MenuItem>
              <MenuItem value={INVOICE_STATUSES.OVERDUE}>{INVOICE_STATUSES.OVERDUE}</MenuItem>
              <MenuItem value={INVOICE_STATUSES.UNPAID}>{INVOICE_STATUSES.UNPAID}</MenuItem>
            </TextField>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 2 }}>
            <TextField 
              label="Issue Date" 
              type="date" 
              fullWidth 
              {...register('issueDate')} 
              InputLabelProps={{ shrink: true }} 
              error={!!errors.issueDate} 
              helperText={errors.issueDate?.message} 
            />
            <TextField 
              label="Due Date" 
              type="date" 
              fullWidth 
              {...register('dueDate')} 
              InputLabelProps={{ shrink: true }} 
              error={!!errors.dueDate} 
              helperText={errors.dueDate?.message} 
            />
            <TextField 
              label="Paid Date" 
              type="date" 
              fullWidth 
              {...register('paidDate')} 
              InputLabelProps={{ shrink: true }} 
              error={!!errors.paidDate} 
              helperText={errors.paidDate?.message} 
            />
          </Box>

          <TextField 
            label="Notes" 
            fullWidth 
            multiline 
            rows={2}
            {...register('notes')}
            InputLabelProps={{ shrink: true }} 
            error={!!errors.notes} 
            helperText={errors.notes?.message} 
          />

           <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, my: 2 }}>
             <TextField 
               label="Month Filter" 
               select 
               fullWidth 
               value={selectedMonth}
               onChange={(e) => setSelectedMonth(e.target.value)}
               helperText="Filter timesheet data by month"
             >
               {getMonthOptions().map(option => (
                 <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
               ))}
             </TextField>
             <TextField 
               label="RSD Amount" 
               type="number"
               fullWidth 
               {...register('rsdAmount')}
               error={!!errors.rsdAmount} 
               helperText={errors.rsdAmount?.message || "Enter the invoice amount in RSD for reporting purposes"}
               InputLabelProps={{ shrink: true }}
             />
           </Box>
        </Box>

        <Box sx={{ my: 3, p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Invoice Items</Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={addItem}
              variant="outlined"
              size="small"
            >
              Add Item
            </Button>
          </Box>

          {fields.map((field, index) => (
            <Box key={field.id} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.default' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2">Item {index + 1}</Typography>
                {fields.length > 1 && (
                  <IconButton 
                    onClick={() => removeItem(index)} 
                    color="error" 
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 2 }}>
                <TextField 
                  label="Description" 
                  fullWidth 
                  {...register(`items.${index}.description`)}
                  error={!!errors.items?.[index]?.description}
                  helperText={errors.items?.[index]?.description?.message}
                />
                
                <TextField 
                  label="Unit" 
                  select 
                  fullWidth 
                  {...register(`items.${index}.unit`)}
                  value={watch(`items.${index}.unit`) || UNITS.HOUR}
                >
                  <MenuItem value={UNITS.HOUR}>{UNITS.HOUR}</MenuItem>
                  <MenuItem value={UNITS.DAY}>{UNITS.DAY}</MenuItem>
                  <MenuItem value={UNITS.MONTH}>{UNITS.MONTH}</MenuItem>
                  <MenuItem value={UNITS.PIECE}>{UNITS.PIECE}</MenuItem>
                  <MenuItem value={UNITS.SERVICE}>{UNITS.SERVICE}</MenuItem>
                </TextField>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                <TextField 
                  label="Price" 
                  type="number" 
                  fullWidth 
                  {...register(`items.${index}.unitPrice`)}
                  error={!!errors.items?.[index]?.unitPrice}
                  helperText={errors.items?.[index]?.unitPrice?.message}
                  onChange={(e) => {
                    const price = parseFloat(e.target.value) || 0;
                    const quantity = parseFloat(watch(`items.${index}.quantity`)) || 0;
                    const amount = Math.round((price * quantity) * 100) / 100; // Round to 2 decimal places
                    setValue(`items.${index}.amount`, amount);
                  }}
                />
                
                <TextField 
                  label="Quantity" 
                  type="number" 
                  fullWidth 
                  {...register(`items.${index}.quantity`)}
                  error={!!errors.items?.[index]?.quantity}
                  helperText={errors.items?.[index]?.quantity?.message}
                  onChange={(e) => {
                    const quantity = parseFloat(e.target.value) || 0;
                    const price = parseFloat(watch(`items.${index}.unitPrice`)) || 0;
                    const amount = Math.round((price * quantity) * 100) / 100; // Round to 2 decimal places
                    setValue(`items.${index}.amount`, amount);
                  }}
                />
                
                <TextField 
                  label="Amount" 
                  type="number" 
                  fullWidth 
                  value={watch(`items.${index}.amount`) || 0}
                  InputProps={{ readOnly: true }}
                  sx={{ '& .MuiInputBase-input': { bgcolor: 'action.hover' } }}
                />
              </Box>
            </Box>
          ))}

          <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1">Subtotal:</Typography>
              <Typography variant="body1">{formatNumber(calculateSubtotal())} {watchedCurrency}</Typography>
            </Box>
            {calculatePDV() > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">PDV (20%):</Typography>
                <Typography variant="body1">{formatNumber(calculatePDV())} {watchedCurrency}</Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
              <Typography variant="h6">Total:</Typography>
              <Typography variant="h6">{formatNumber(calculateTotal())} {watchedCurrency}</Typography>
            </Box>
          </Box>
        </Box>

        <Button 
          type="submit" 
          variant="contained" 
          color="primary" 
          fullWidth 
          sx={{ mt: 2 }} 
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Adding...' : 'Add Invoice'}
        </Button>
      </form>
    </Box>
  );
};

export default AddInvoice; 